import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { ArrowUpRight, Menu, X } from "lucide-react";
import { VB_URL } from "../lib/bopta";
import { Brand } from "./Brand";
import { ScrollTop, toTop } from "./ScrollTop";

const NAV = [["/", "Overview", "overview"], ["/framework", "Framework", "framework"], ["/my-results", "My results", "my-results"], ["/employers", "For employers", "employers"]];

const Logo = ({ className = "h-9 lg:h-14" }) => (
  <Link to="/" onClick={toTop} className="flex items-center" data-testid="nav-logo">
    <img src="/logo.png" alt="mpwrd. Talent Autonomy" className={`${className} w-auto`} />
  </Link>
);

const Header = () => {
  const [open, setOpen] = useState(false);
  const linkCls = ({ isActive }) => `rounded-lg px-3 py-2 text-sm font-medium ${isActive ? "bg-[var(--tint)] text-[var(--teal)]" : "muted hover:text-[var(--ink)]"}`;
  return (
    <header className="sticky top-0 z-40 border-b border-[var(--line)] bg-white no-print">
      <div className="wrap flex h-16 items-center justify-between lg:h-24">
        <Logo />
        <nav className="hidden items-center gap-1 md:flex">
          {NAV.map(([to, l, id]) => <NavLink key={to} to={to} end className={linkCls} data-testid={`nav-${id}`}>{l}</NavLink>)}
        </nav>
        <div className="hidden items-center gap-3 md:flex">
          <Link to="/assessment" className="btn btn-main" data-testid="nav-start">Take the assessment</Link>
        </div>
        <button className="md:hidden" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-mobile-toggle">{open ? <X /> : <Menu />}</button>
      </div>
      {open && (
        <div className="wrap space-y-1 border-t border-[var(--line)] py-3 md:hidden">
          {NAV.map(([to, l]) => <Link key={to} to={to} onClick={() => setOpen(false)} className="block py-2">{l}</Link>)}
          <Link to="/assessment" onClick={() => setOpen(false)} className="btn btn-main w-full">Take the assessment</Link>
        </div>
      )}
    </header>
  );
};

const Footer = () => (
  <footer className="mt-24 border-t border-[var(--line)] bg-white no-print">
    <div className="wrap grid gap-8 py-12 md:grid-cols-3">
      <div>
        <Logo className="h-12 lg:h-16" />
        <p className="mt-3 max-w-xs text-sm muted">A staff-lens diagnostic connecting daily independence to your role, reporting line and next step.</p>
      </div>
      <div className="space-y-2 text-sm">
        <Link to="/assessment" className="block muted hover:text-[var(--ink)]">Staff assessment</Link>
        <Link to="/assessment?mode=manager" className="block muted hover:text-[var(--ink)]">Manager self-assessment</Link>
        <Link to="/framework" className="block muted hover:text-[var(--ink)]">BOPTA framework</Link>
        <Link to="/employers" className="block muted hover:text-[var(--ink)]">Team 360° dashboard</Link>
        <Link to="/admin" className="block muted hover:text-[var(--ink)]" data-testid="footer-admin">Admin</Link>
      </div>
      <a href={VB_URL} target="_blank" rel="noreferrer" className="flex items-start gap-2 text-sm font-semibold text-[var(--teal)]" data-testid="footer-venturebaseline">
        Part of the VentureBaseline ecosystem <ArrowUpRight className="h-4 w-4" />
      </a>
    </div>
    <p className="wrap pb-8 text-xs muted">© 2026 <Brand /> · A reflection tool, not a performance verdict.</p>
  </footer>
);

export default function Layout() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return (
    <div className="min-h-screen">
      <Header />
      <main><Outlet /></main>
      <Footer />
      <ScrollTop />
    </div>
  );
}
